import React, { useState, useEffect } from "react";

interface Question {
  id: string;
  text: string;
  translatedText?: string;
  studentName: string;
  createdAt: string;
}

interface QuestionsListModalProps {
  isOpen: boolean;
  onClose: () => void;
  classId: string | undefined;
  isProfessor: boolean;
  socket: any; // 소켓 객체 추가
}

const QuestionsListModal: React.FC<QuestionsListModalProps> = ({
  isOpen,
  onClose,
  classId,
  isProfessor,
  socket,
}) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 모달 스크롤 방지 효과
  useEffect(() => {
    if (isOpen) {
      // 스크롤 위치 저장
      const scrollY = window.scrollY;

      // body에 modal-open 클래스 추가
      document.body.classList.add("modal-open");

      // 스크롤 위치 설정
      document.body.style.top = `-${scrollY}px`;

      // 정리 함수
      return () => {
        document.body.classList.remove("modal-open");
        document.body.style.top = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // 질문 목록 소켓으로 불러오기
  const fetchQuestions = () => {
    if (!classId) {
      setError("클래스 ID가 없습니다.");
      setIsLoading(false);
      return;
    }

    if (socket && socket.connected) {
      setIsLoading(true);
      setError(null);

      socket.emit("getQuestions", { classId }, (response: any) => {
        if (response && response.success) {
          setQuestions(response.questions || []);
        } else {
          setError(
            `질문 목록을 불러오는 중 오류가 발생했습니다: ${
              response?.message || "알 수 없는 오류"
            }`
          );
        }
        setIsLoading(false);
      });
    } else {
      setError("소켓 연결이 활성화되지 않았습니다.");
      setIsLoading(false);
    }
  };

  // 모달이 열릴 때마다 질문 목록 갱신
  useEffect(() => {
    if (isOpen && socket && socket.connected) {
      fetchQuestions();
    }
  }, [isOpen, classId, socket, socket?.connected]);

  // 새 질문 이벤트 리스너 설정
  useEffect(() => {
    if (!socket) return;

    // 새 질문 도착 이벤트 핸들러
    const handleNewQuestion = (data: any) => {
      // 이미 모달이 열려있는 경우 질문 목록에 추가
      if (isOpen) {
        setQuestions((prevQuestions) => {
          // 이미 있는 질문인지 확인
          const exists = prevQuestions.some(
            (q) =>
              q.id === data.questionId ||
              (q.text === data.text && q.studentName === data.studentName)
          );
          if (exists) return prevQuestions;

          // 새 질문을 목록의 앞부분에 추가
          return [
            {
              id: data.questionId || `temp-${Date.now()}`,
              text: data.text,
              translatedText: data.translatedText,
              studentName: data.studentName,
              createdAt: new Date().toISOString(),
            },
            ...prevQuestions,
          ];
        });
      }
    };

    // 소켓 이벤트 리스너 등록
    socket.on("newQuestion", handleNewQuestion);

    // 클린업 함수
    return () => {
      socket.off("newQuestion", handleNewQuestion);
    };
  }, [socket, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container questions-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>질문 목록</h3>
          <button className="modal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          {isLoading ? (
            <div className="questions-loading">
              <div className="loading-spinner small"></div>
              <p>질문 목록을 불러오는 중입니다...</p>
            </div>
          ) : error ? (
            <div className="questions-error">
              <p>{error}</p>
              <button className="btn-retry" onClick={fetchQuestions}>
                다시 시도
              </button>
            </div>
          ) : questions.length === 0 ? (
            <div className="no-questions">
              <p>아직 질문이 없습니다.</p>
            </div>
          ) : (
            <div className="questions-list">
              {questions.map((question) => (
                <div key={question.id} className="question-item">
                  <div className="question-header">
                    <span className="student-name">
                      {question.studentName || "익명"}
                    </span>
                    <span className="question-time">
                      {new Date(question.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="question-text">
                    {question.text}
                    {question.translatedText && (
                      <div
                        className="translated-text"
                        style={{
                          marginTop: "8px",
                          paddingLeft: "10px",
                          borderLeft: "2px solid #ddd",
                          color: "#555",
                          fontStyle: "italic",
                        }}
                      >
                        {question.translatedText}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-primary" onClick={onClose}>
            닫기
          </button>
          <button className="btn-refresh" onClick={fetchQuestions}>
            새로고침
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionsListModal;
